import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { sellerService } from '../../../services/sellerService';
import { manufacturingService } from '../../../services/manufacturingService';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select'; // Assuming a Select component exists
import CollapsibleSection from './CollapsibleSection'; // Import the new CollapsibleSection
import { countries, countryOptions } from '../../../utils/countries';
import { formatPrice } from '../../../utils/formatters'; // Assuming countries utility exists
import StripePaymentForm from '../../../components/ui/StripePaymentForm';
import { userPaymentMethodService } from '../../../services/userPaymentMethodService';
import { supabase } from '../../../lib/supabase';
import TierUpgradeModal from './TierUpgradeModal';
import toast from 'react-hot-toast';

// Utility to convert snake_case to camelCase string
const snakeToCamel = (s) => s.replace(/(_\w)/g, (m) => m[1].toUpperCase());

const SellerSettingsTab = ({ onAccountUpdate, isManufacturer = false }) => {
  const { user } = useAuth();
  const [sellerAccount, setSellerAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingBanking, setIsEditingBanking] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isAssemblySharingEnabled, setIsAssemblySharingEnabled] = useState(false); // State for Assembly Sharing toggle
  const [preferredLayout, setPreferredLayout] = useState(() => localStorage.getItem('seller_preferred_layout') || 'list');
  const [preferredLabelType, setPreferredLabelType] = useState(() => localStorage.getItem('seller_preferred_label_type') || 'PAPER_85X11_TOP_HALF_LABEL');
  const [sellerOrderEmailPref, setSellerOrderEmailPref] = useState('detailed');

  const handlePreferredLayoutChange = (e) => {
    const val = e.target.value;
    setPreferredLayout(val);
    localStorage.setItem('seller_preferred_layout', val);
    toast.success(`Default layout preference set to ${val === 'grid' ? 'Cards View' : 'List View'}!`);
  };

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  useEffect(() => {
    const fetchSellerAccount = async () => {
      if (!user?.id) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        if (isManufacturer) {
          const { data, error: mfgErr } = await supabase
            .from('manufacturer_accounts')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

          if (mfgErr) throw mfgErr;
          const camelData = data ? {
            id: data.id,
            tier: data.tier || 'workshop',
            businessName: data.business_name,
            businessEmail: data.business_email,
            businessPhone: data.business_phone,
            accountStatus: data.account_status,
            monthlyServiceFee: data.monthly_service_fee,
            subscriptionExpiresAt: data.subscription_expires_at,
            isDeleted: Boolean(data.is_deleted || data.isdeleted),
            isManufacturer: true
          } : {};
          setSellerAccount(camelData);
          setFormData(camelData);
        } else {
          const data = await sellerService.getSellerAccountForUser(user.id);
          const camelData = data ? {
            ...data,
            isDeleted: Boolean(data.is_deleted || data.isdeleted || data.isDeleted)
          } : {};
          setSellerAccount(camelData);
          setFormData(camelData); // Initialize form data with fetched data
          setIsAssemblySharingEnabled(data?.assemblySharingEnabled || false); // Initialize from fetched data
        }
        
        // Also fetch payment methods
        const methods = await userPaymentMethodService.getUserPaymentMethods(user.id);
        setPaymentMethods(methods?.filter(m => m.isBusiness) || []);
        
        setError('');
      } catch (err) {
        console.error('Error fetching seller account:', err);
        setError(err.message || 'Failed to fetch seller account details.');
      } finally {
        setLoading(false);
      }
    };

    fetchSellerAccount();
  }, [user?.id, isManufacturer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const camelCaseName = snakeToCamel(name);
    setFormData((prev) => ({ ...prev, [camelCaseName]: value }));
  };

  const handleCancelAccount = async () => {
    setIsSaving(true);
    try {
      if (isManufacturer) {
        const { error: mfgErr } = await supabase
          .from('manufacturer_accounts')
          .update({
            account_status: 'inactive',
            monthly_service_fee: 0,
            is_deleted: true
          })
          .eq('id', sellerAccount.id);

        if (mfgErr) throw mfgErr;
        
        setSellerAccount(prev => ({
          ...prev,
          accountStatus: 'inactive',
          monthlyServiceFee: 0,
          isDeleted: true
        }));
        toast.success('Manufacturing account cancelled successfully!');
      } else {
        const { error: sellerErr } = await supabase
          .from('seller_accounts')
          .update({
            store_status: 'inactive',
            is_deleted: true
          })
          .eq('id', sellerAccount.id);

        if (sellerErr) throw sellerErr;

        setSellerAccount(prev => ({
          ...prev,
          storeStatus: 'inactive',
          accountStatus: 'inactive',
          isDeleted: true
        }));
        toast.success('Seller store account cancelled successfully!');
      }
    } catch (err) {
      console.error('Failed to cancel account:', err);
      toast.error('Failed to cancel account: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCountryChange = (val) => {
    setFormData((prev) => ({ ...prev, country: val }));
  };

  const handleSaveContactAndAddress = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      const updatedData = {
        business_email: formData.businessEmail,
        business_phone: formData.businessPhone,
        website: formData.website,
        seller_first_name: formData.sellerFirstName,
        seller_last_name: formData.sellerLastName,
        address_line_1: formData.addressLine1,
        address_line_2: formData.addressLine2,
        city: formData.city,
        state_province: formData.stateProvince,
        postal_code: formData.postalCode,
        country: formData.country,
      };
      
      await sellerService.updateSellerAccountForUser(user.id, updatedData);
      setSellerAccount((prev) => ({ ...prev, ...updatedData })); // Update local state
      setIsEditingContact(false);
      alert('Contact & Address details updated successfully!');
    } catch (err) {
      console.error('Error updating contact & address details:', err);
      setError(err.message || 'Failed to update contact & address details.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBankingInfo = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      const updatedData = {
        bank_name: formData.bankName,
        bank_routing_number: formData.bankRoutingNumber,
        bank_account_number: formData.bankAccountNumber,
      };
      
      await sellerService.updateSellerAccountForUser(user.id, updatedData);
      setSellerAccount((prev) => ({ ...prev, ...updatedData })); // Update local state
      setIsEditingBanking(false);
      alert('Banking information updated successfully!');
    } catch (err) {
      console.error('Error updating banking information:', err);
      setError(err.message || 'Failed to update banking information.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-4"></div>
        <p className="text-muted-foreground">Loading store details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4 text-destructive-foreground">
        <Icon name="AlertCircle" className="inline mr-2" />
        {error}
      </div>
    );
  }

  if (!sellerAccount) {
    return (
      <div className="bg-warning/10 border border-warning rounded-lg p-4 text-warning-foreground">
        <Icon name="AlertTriangle" className="inline mr-2" />
        No seller account found.
      </div>
    );
  }

  const renderReadOnlyField = (label, value) => (
    <div className="flex flex-col space-y-1">
      <label className="text-sm font-medium text-muted-foreground">{label}</label>
      <p className="text-base text-foreground">{value || 'N/A'}</p>
    </div>
  );

  const getSubscriptionIconAndColor = (tier) => {
    switch (tier) {
      case 'entry': return { iconName: 'Star', colorClass: 'text-gray-500 bg-gray-100' };
      case 'mid_tier': return { iconName: 'TrendingUp', colorClass: 'text-indigo-600 bg-indigo-100' };
      case 'platinum': return { iconName: 'Gem', colorClass: 'text-yellow-600 bg-yellow-100' };
      default: return { iconName: 'Award', colorClass: 'text-muted-foreground bg-gray-50' };
    }
  };

  const getBankStatusDisplay = (status) => {
    switch (status) {
      case 'verified': return { iconName: 'CheckCircle', label: 'Verified', colorClass: 'text-success bg-success/10' };
      case 'pending': return { iconName: 'Hourglass', label: 'Pending', colorClass: 'text-warning bg-warning/10' };
      case 'submitted': return { iconName: 'Clock', label: 'Submitted', colorClass: 'text-info bg-info/10' };
      case 'invalid': return { iconName: 'AlertTriangle', label: 'Invalid', colorClass: 'text-destructive bg-destructive/10' };
      case 'error': return { iconName: 'XCircle', label: 'Error', colorClass: 'text-destructive bg-destructive/10' };
      default: return { iconName: 'HelpCircle', label: 'Unknown', colorClass: 'text-muted-foreground bg-muted/10' };
    }
  };
  
  const bankStatusTooltips = {
    submitted: 'You have submitted your banking information and it is awaiting review.',
    pending: 'Your banking information is currently under review.',
    verified: 'Your banking information has been successfully verified.',
    invalid: 'The banking information you provided is invalid. Please check and re-submit.',
    error: 'An unexpected error occurred during the verification process. Please contact support.',
    unknown: 'The status of your banking information is unknown.',
  };

  const { iconName, colorClass } = getSubscriptionIconAndColor(sellerAccount.tier);

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-foreground">{sellerAccount.businessName || 'My Store'}</h2>
          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${colorClass} bg-current/10`}>
            <Icon name={iconName} size={16} />
            <span>{sellerService.getTierInfo(sellerAccount.tier)?.name}</span>
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto"
            onClick={() => setShowUpgradeModal(true)}
          >
            Upgrade Plan
          </Button>
        </div>
      </div>

      {/* Contact & Address Section */}
      <CollapsibleSection title="Contact & Address" defaultOpen={true}>
        <form onSubmit={handleSaveContactAndAddress} className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-foreground">Contact & Address</h3>
            {!isEditingContact && (
              <Button onClick={() => setIsEditingContact(true)} iconName="Edit" iconPosition="left">
                Edit
              </Button>
            )}
          </div>
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Contact Person</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="First Name" name="seller_first_name" value={formData.sellerFirstName || ''} onChange={handleChange} readOnly={!isEditingContact} />
              <Input label="Last Name" name="seller_last_name" value={formData.sellerLastName || ''} onChange={handleChange} readOnly={!isEditingContact} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Business Email" name="business_email" value={formData.businessEmail || ''} onChange={handleChange} readOnly={!isEditingContact} type="email" />
            <Input label="Business Phone" name="business_phone" value={formData.businessPhone || ''} onChange={handleChange} readOnly={!isEditingContact} type="tel" />
            <Input label="Website" name="website" value={formData.website || ''} onChange={handleChange} readOnly={!isEditingContact} type="url" />
            <div className="md:col-span-2"><Input label="Address Line 1" name="address_line_1" value={formData.addressLine1 || ''} onChange={handleChange} readOnly={!isEditingContact} /></div>
            <div className="md:col-span-2"><Input label="Address Line 2" name="address_line_2" value={formData.addressLine2 || ''} onChange={handleChange} readOnly={!isEditingContact} /></div>
            <Input label="City" name="city" value={formData.city || ''} onChange={handleChange} readOnly={!isEditingContact} />
            <Input label="State/Province" name="state_province" value={formData.stateProvince || ''} onChange={handleChange} readOnly={!isEditingContact} />
            <Input label="Zip/Postal Code" name="postal_code" value={formData.postalCode || ''} onChange={handleChange} readOnly={!isEditingContact} />
            <Select 
              label="Country" 
              name="country" 
              value={formData.country || ''} 
              onChange={handleCountryChange} 
              disabled={!isEditingContact} 
              options={countryOptions} 
            />
          </div>

          {isEditingContact && (
            <div className="flex justify-end space-x-2 border-t border-border pt-4">
              <Button variant="outline" onClick={() => { setIsEditingContact(false); setFormData(sellerAccount); }} type="button">Cancel</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? (<><Icon name="Loader2" className="animate-spin mr-2" />Saving...</>) : 'Save Changes'}</Button>
            </div>
          )}
        </form>
      </CollapsibleSection>

      {/* Banking Information Section */}
      <CollapsibleSection title="Banking Information">
        <form onSubmit={handleSaveBankingInfo} className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-foreground">Banking Information</h3>
            <div className="flex items-center gap-4">
                {sellerAccount.bankVerificationStatus && (
                    (() => {
                        const { iconName: bankIcon, label: bankLabel, colorClass: bankColor } = getBankStatusDisplay(sellerAccount.bankVerificationStatus);
                        const tooltipText = bankStatusTooltips[sellerAccount.bankVerificationStatus] || bankStatusTooltips.unknown;
                        return (
                            <div title={tooltipText}>
                                <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${bankColor}`}>
                                    <Icon name={bankIcon} size={16} />
                                    <span>{bankLabel}</span>
                                    <Icon name="HelpCircle" size={14} className="ml-1 opacity-75" />
                                </span>
                            </div>
                        );
                    })()
                )}
                {!isEditingBanking && (
                    <Button onClick={() => setIsEditingBanking(true)} iconName="Edit" iconPosition="left">
                        Edit
                    </Button>
                )}
            </div>
          </div>

          {isEditingBanking && (
            <div className="bg-warning/10 border border-warning rounded-lg p-4 text-warning my-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <Icon name="AlertTriangle" className="h-5 w-5 text-warning" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-warning">Heads up!</h3>
                        <div className="mt-2 text-sm text-warning">
                            <p>
                                Updating your banking details will trigger a re-verification process and may temporarily delay payouts.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Bank Name" name="bank_name" value={formData.bankName || ''} onChange={handleChange} readOnly={!isEditingBanking} />
            <Input label="Bank Routing Number" name="bank_routing_number" value={formData.bankRoutingNumber || ''} onChange={handleChange} readOnly={!isEditingBanking} />
            <Input label="Bank Account Number" name="bank_account_number" value={formData.bankAccountNumber || ''} onChange={handleChange} readOnly={!isEditingBanking} />
          </div>

          {isEditingBanking && (
            <div className="flex justify-end space-x-2 border-t border-border pt-4">
              <Button variant="outline" onClick={() => { setIsEditingBanking(false); setFormData(sellerAccount); }} type="button">Cancel</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? (<><Icon name="Loader2" className="animate-spin mr-2" />Saving...</>) : 'Save Changes'}</Button>
            </div>
          )}
        </form>
      </CollapsibleSection>
      
      {/* Payment Methods Section */}
      <CollapsibleSection title="Payment Methods (Subscription Billing)">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-foreground">Stored Credit Cards</h3>
              <p className="text-sm text-muted-foreground mt-1">Manage cards used for your monthly seller subscription.</p>
            </div>
            {!showPaymentForm && (
              <Button onClick={() => setShowPaymentForm(true)} iconName="Plus" iconPosition="left" size="sm">
                Add Card
              </Button>
            )}
          </div>

          {paymentMethods.length > 0 && !showPaymentForm ? (
            <div className="grid gap-4">
              {paymentMethods.map(method => (
                <div key={method.id} className="flex justify-between items-center p-4 border border-border rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-12 bg-white rounded border border-gray-200 flex items-center justify-center">
                      <Icon name="CreditCard" size={20} className="text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {method.cardBrand} ending in {method.cardLastFour}
                      </p>
                      <p className="text-sm text-gray-500">Expires {method.cardExpMonth}/{method.cardExpYear}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={async () => {
                      if(window.confirm('Remove this payment method?')) {
                        try {
                          await userPaymentMethodService.deletePaymentMethod(method.id);
                          setPaymentMethods(prev => prev.filter(p => p.id !== method.id));
                        } catch (err) {
                          alert('Failed to remove payment method');
                        }
                      }
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              ))}
            </div>
          ) : !showPaymentForm ? (
             <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg">
               <Icon name="CreditCard" size={32} className="mx-auto text-gray-400 mb-2" />
               <p className="text-gray-500">No payment methods saved.</p>
             </div>
          ) : null}

          {showPaymentForm && (
            <div className="p-4 border border-border rounded-lg bg-gray-50 mt-4">
              <h4 className="font-medium mb-4">Add New Card</h4>
              <StripePaymentForm 
                userEmail={user?.email}
                buttonText="Save Card"
                onSuccess={async (paymentData) => {
                  try {
                    const businessPaymentData = { ...paymentData, isBusiness: true };
                    await userPaymentMethodService.createPaymentMethod(user.id, businessPaymentData);
                    const methods = await userPaymentMethodService.getUserPaymentMethods(user.id);
                    setPaymentMethods(methods?.filter(m => m.isBusiness) || []);
                    setShowPaymentForm(false);
                  } catch(err) {
                    alert('Failed to save card: ' + err.message);
                  }
                }}
                onCancel={() => setShowPaymentForm(false)}
              />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* API Access Section */}
      <CollapsibleSection title="API Access">
        <div className="space-y-4">
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-medium text-foreground">API Keys</h4>
                <p className="text-xs text-muted-foreground">Manage your secret API keys to authenticate with our services.</p>
              </div>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                iconName="Plus" 
                onClick={() => alert('API Key generation coming soon!')}
                disabled={sellerAccount?.tier !== 'platinum'}
                title={sellerAccount?.tier !== 'platinum' ? 'API access requires Platinum tier' : ''}
              >
                Generate New Key
              </Button>
            </div>
            
            {sellerAccount?.tier !== 'platinum' ? (
              <div className="bg-warning/10 border border-warning/20 rounded-md p-4 text-sm text-warning flex items-start gap-3">
                <Icon name="Lock" className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">Platinum Tier Required</p>
                  <p className="opacity-90">Upgrade your account to Platinum to generate API keys and integrate directly with your systems.</p>
                </div>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-md p-4 text-center text-sm text-muted-foreground border border-dashed border-border">
                No API keys generated yet.
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Capabilities & Management */}
      <CollapsibleSection title="Capabilities & Management">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {renderReadOnlyField('Self-Fulfill Capability', sellerAccount.canSelfFulfill ? 'Yes' : 'No')}
          {renderReadOnlyField('Strategic Account Manager', sellerAccount.strategic_account_manager)}
          {renderReadOnlyField('Use Supplier', sellerAccount.can_user_suppliers ? 'Yes' : 'No')}
        </div>
      </CollapsibleSection>

      {/* Symmetrical Preferences Section */}
      <CollapsibleSection title="Seller Dashboard Preferences">
        <div className="p-4 space-y-4 text-left grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Default Dashboard View</label>
            <p className="text-xs text-slate-500 mb-3">Choose which visual format should load by default inside your Products and Offered Components management subtabs.</p>
            <div className="w-full">
              <select
                value={preferredLayout}
                onChange={handlePreferredLayoutChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white text-gray-800 font-semibold cursor-pointer"
              >
                <option value="list">List View (Table style)</option>
                <option value="grid">Cards View (Visual grid style)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Printer Label Type ("Print & Confirm")</label>
            <p className="text-xs text-slate-500 mb-3">Configure your local shipping label format for Laser/Inkjet printers (8.5x11) or Thermal Label printers (4x6).</p>
            <div className="w-full">
              <select
                value={preferredLabelType}
                onChange={(e) => {
                  const val = e.target.value;
                  setPreferredLabelType(val);
                  localStorage.setItem('seller_preferred_label_type', val);
                  toast.success(`Printer preference set to ${val === 'STOCK_4X6' ? '4" x 6" Thermal Labels' : '8.5" x 11" Laser/Inkjet'}!`);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white text-gray-800 font-semibold cursor-pointer"
              >
                <option value="PAPER_85X11_TOP_HALF_LABEL">Laser/Inkjet Printer (8.5" x 11" Paper)</option>
                <option value="STOCK_4X6">Thermal Label Printer (4" x 6" Label)</option>
              </select>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Notification Settings Section */}
      <CollapsibleSection title="Notification Settings">
        <div className="p-4 space-y-4 text-left bg-indigo-50/10 border-indigo-100/50 rounded-lg">
          <div>
            <label className="block text-xs font-bold text-indigo-900 uppercase tracking-wider mb-2">New Orders Email Format</label>
            <p className="text-xs text-indigo-700/80 mb-3">Configure how you receive automated email alerts for new order creations on your inventory.</p>
            <div className="w-full max-w-xs">
              <select
                value={sellerOrderEmailPref}
                onChange={(e) => {
                  setSellerOrderEmailPref(e.target.value);
                  toast.success(`Order email alert format updated to ${e.target.value}!`);
                }}
                className="w-full px-3 py-2 border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white text-indigo-900 font-semibold"
              >
                <option value="detailed">Detailed email</option>
                <option value="simplified">Simplified email</option>
                <option value="none">No email</option>
              </select>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Admin-Managed Information */}
      <CollapsibleSection title="Admin-Managed Information">
        <p className="text-muted-foreground text-sm">These details can only be updated by an administrator.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {!isManufacturer && renderReadOnlyField('Business EIN', sellerAccount.businessEin)}
          {!isManufacturer && renderReadOnlyField('Business CRN', sellerAccount.businessCrn)}
          {renderReadOnlyField('Account Status', sellerAccount.accountStatus)}
          {renderReadOnlyField('Subscription Type', isManufacturer ? manufacturingService.getTierInfo(sellerAccount.tier)?.name : sellerService.getTierInfo(sellerAccount.tier)?.name)}
          {renderReadOnlyField('Monthly Fee', formatPrice(isManufacturer ? manufacturingService.getTierInfo(sellerAccount.tier)?.price : sellerService.getTierInfo(sellerAccount.tier)?.price))}
          {!isManufacturer && renderReadOnlyField('Total Products', sellerAccount.totalProducts?.toLocaleString())}
          {!isManufacturer && renderReadOnlyField('Total Sales', formatPrice(sellerAccount.totalSales))}
        </div>
      </CollapsibleSection>

      {/* Danger Zone / Account Cancellation Section */}
      <CollapsibleSection title="Danger Zone (Account & Billing Management)">
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 p-5 rounded-xl text-left">
            <h4 className="text-sm font-bold text-red-800 flex items-center gap-2 mb-2">
              <span>⚠️</span> Cancel Service Tier / Account Offering
            </h4>
            <p className="text-xs text-red-700 leading-relaxed mb-4">
              Disabling this offering will immediately remove your seller products and components from the public catalog search. Your custom digital printing/CNC options will be disabled, and any pending order fulfillments will need to be settled. Your global catalog profile will remain intact for history.
            </p>
            
            {sellerAccount?.isDeleted ? (
              <div className="bg-white border border-red-300 rounded-lg p-4 shadow-sm text-xs font-semibold text-red-800 leading-relaxed">
                📢 Your {isManufacturer ? 'manufacturing' : 'store'} subscription has been successfully cancelled. Your account valid status remains active until your billing expiration date on: <span className="underline">{sellerAccount.subscriptionExpiresAt ? new Date(sellerAccount.subscriptionExpiresAt).toLocaleDateString() : 'N/A'}</span>. Billing configs and monthly service fees have been removed.
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs text-gray-500 font-bold">
                    Account Status: <span className="text-success uppercase">{sellerAccount?.accountStatus || 'Active'}</span>
                  </p>
                  {sellerAccount?.subscriptionExpiresAt && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Valid/Active until: {new Date(sellerAccount.subscriptionExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="danger"
                  className="bg-red-600 hover:bg-red-700 border-b-2 border-red-800 font-black text-[10px] uppercase tracking-wider h-10 px-6 shadow-md"
                  onClick={() => setShowCancelModal(true)}
                  disabled={isSaving}
                >
                  {isSaving ? 'CANCELING...' : 'CANCEL SUBSCRIPTION'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 text-left animate-in zoom-in-95">
            <h3 className="text-lg font-black text-gray-900 flex items-center gap-2 mb-3">
              <span className="text-red-600">⚠️</span> Cancel Service Subscription?
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-6">
              {isManufacturer
                ? "Are you sure you want to cancel your digital manufacturing service subscription? Your buyer account will remain active, but your manufacturing offerings and billing configurations will be deactivated."
                : "Are you sure you want to cancel your store subscription? Your buyer account will remain active, but your products/components listings and store billing will be deactivated."}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-10 px-4 text-xs font-black uppercase"
                onClick={() => setShowCancelModal(false)}
                disabled={isSaving}
              >
                Keep Active
              </Button>
              <Button
                type="button"
                variant="danger"
                className="h-10 px-4 text-xs font-black bg-red-600 hover:bg-red-700 text-white uppercase"
                onClick={async () => {
                  await handleCancelAccount();
                  setShowCancelModal(false);
                }}
                disabled={isSaving}
              >
                {isSaving ? 'CANCELING...' : 'YES, CANCEL'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <TierUpgradeModal
          currentTier={sellerAccount.tier}
          accountId={sellerAccount.id}
          isManufacturer={isManufacturer}
          onClose={() => setShowUpgradeModal(false)}
          onUpgradeSuccess={(newTier) => {
            setSellerAccount(prev => ({ ...prev, tier: newTier }));
            if (onAccountUpdate) onAccountUpdate();
          }}
        />
      )}
    </div>
  );
};

export default React.memo(SellerSettingsTab);